import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './Expenses.css';
import ExpenseForm from './AddExpenses'; 
import UpdateExpenses from './UpdateExpenses'; 
import { FaSearch, FaTrash, FaEdit, FaCheck, FaTimes, FaClock, FaEye } from "react-icons/fa";
import { API_URL,  getImageUrl } from './API';

const ExpenseList = ({ 
  project, 
  goBack, 
  onExpenseAdded, 
  onExpenseUpdated, 
  onExpenseDeleted 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddExpenseForm, setShowAddExpenseForm] = useState(false); 
  const [showUpdateExpenseForm, setShowUpdateExpenseForm] = useState(false); 
  const [selectedExpenseForUpdate, setSelectedExpenseForUpdate] = useState(null); 
  const [editingId, setEditingId] = useState(null); 
  const [editedExpense, setEditedExpense] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [employeeLookup, setEmployeeLookup] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedFromDate, setSelectedFromDate] = useState('');
  const [selectedToDate, setSelectedToDate] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const hasFetchedRef = useRef(false);
  const currentProjectIdRef = useRef(null);
  const isMountedRef = useRef(true);

  const getProjectId = useCallback(() => {
    if (!project) return null;
    return project.project_id || project.id;
  }, [project]);

 const getCurrentUserRole = () => {
  const accessLevel = localStorage.getItem('accessLevel');
  const userRole = localStorage.getItem('currentUserRole') || '';
  
  if (userRole.toUpperCase() === 'CEO' || userRole.toUpperCase().includes('CEO')) {
    return 'CEO';
  }
  
  if (accessLevel === 'full' || 
      userRole.toUpperCase() === 'ADMIN' ||
      userRole.toUpperCase().includes('ADMIN')) {
    return 'ADMIN';
  }
  
  return 'EMPLOYEE';
};

  const getCurrentUserId = () => {
    return localStorage.getItem('currentUserId');
  };

  const getCurrentUserName = () => {
    return localStorage.getItem('currentUserName') || 'Current User';
  };

  const getProjectName = useCallback(() => {
    if (!project) return 'All Expenses';
    return project.project_name || project.name || 'Project Expenses';
  }, [project]);

  const projectId = useMemo(() => getProjectId(), [getProjectId]);
  const projectName = useMemo(() => getProjectName(), [getProjectName]);

  const getAvailableReasons = useCallback(() => {
    const reasons = expenses.map(expense => expense.reason).filter(Boolean);
    return [...new Set(reasons)].sort();
  }, [expenses]);

const getAvailableEmployees = useCallback(() => {
  const userRole = getCurrentUserRole();
  const currentUserId = getCurrentUserId();
  const currentUserName = getCurrentUserName();

  
  if (userRole === 'EMPLOYEE') {
    return [{
      id: currentUserId,
      name: currentUserName
    }];
  }


  const employees = expenses.map(expense => ({
    id: expense.employeeId,
    name: expense.employeeName
  }));
  const uniqueEmployees = Array.from(
    new Map(employees.map(emp => [emp.id, emp])).values()
  );
  return uniqueEmployees.sort((a, b) => a.name.localeCompare(b.name));
}, [expenses]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const employeeValue = expense.employeeName || expense.employee_name || '';
      const reasonValue = expense.reason || expense.description || '';
      
      const employeeString = String(employeeValue).toLowerCase();
      const reasonString = String(reasonValue).toLowerCase();
      const searchString = searchTerm.toLowerCase();
      
      const matchesSearch = employeeString.includes(searchString) || reasonString.includes(searchString);
      
      if (selectedEmployee && String(expense.employeeId) !== selectedEmployee) {
        return false;
      }
      
      if (selectedFromDate) {
        const expenseDate = new Date(expense.date);
        const fromDate = new Date(selectedFromDate);
        if (expenseDate < fromDate) return false;
      }
      
      if (selectedToDate) {
        const expenseDate = new Date(expense.date);
        const toDate = new Date(selectedToDate);
        if (expenseDate > toDate) return false;
      }
      
      if (selectedReason && expense.reason !== selectedReason) {
        return false;
      }
      
      if (selectedStatus && expense.status.toLowerCase() !== selectedStatus.toLowerCase()) {
        return false;
      }
      
      return matchesSearch;
    });
  }, [expenses, searchTerm, selectedEmployee, selectedFromDate, selectedToDate, selectedReason, selectedStatus]);

  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  }, [filteredExpenses]);

  const getEmployeeClaimCount = useCallback((employeeId) => {
    return expenses.filter(expense => String(expense.employeeId) === String(employeeId)).length;
  }, [expenses]);

  const getStatusClass = useCallback((status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'EXPEN-06';
      case 'approve':
      case 'approved':
        return 'EXPEN-07';
      case 'disapprove':
      case 'rejected':
        return 'EXPEN-08';
      default:
        return '';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <FaClock className="EXPEN-status-icon" />;
      case 'approve':
      case 'approved':
        return <FaCheck className="EXPEN-status-icon" />;
      case 'disapprove':
      case 'rejected':
        return <FaTimes className="EXPEN-status-icon" />;
      default:
        return null;
    }
  }, []);

  const fetchEmployeeLookup = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/employees/get_employees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (response.ok) {
        const result = await response.json();
        const employeesData = result.employees || result.data || result || [];
        
        const lookup = {};
        employeesData.forEach(emp => {
          const id = emp.employee_id || emp.id;
          const name = emp.employee_name || emp.name || `Employee ${id}`;
          lookup[id] = name;
        });
        
        return lookup;
      }
      
      return {};
    } catch (error) {
      
      return {};
    }
  }, []);

const formatExpenseData = useCallback((expense, index, lookup) => {
  const employeeId = expense.employee_id;
  const employeeName = expense.employee_name || lookup[employeeId] || `Employee ${employeeId}`;
  
  
  const expenseImagePath = expense.expense_image || '';
  const fullImageUrl = expenseImagePath ? getImageUrl(expenseImagePath) : '';
  
 
  
  return {
    id: expense.expense_id || expense.id || index + 1,
    expense_id: expense.expense_id || expense.id,
    employeeId: employeeId,
    employeeName: employeeName,
    date: expense.date_of_expense
      ? expense.date_of_expense.split('T')[0]
      : expense.expense_date
      ? (expense.expense_date.split('T')[0] || expense.expense_date.split(' ')[0])
      : (expense.date || new Date().toISOString().split('T')[0]),
    reason: expense.expense_reason || expense.description || expense.reason || expense.expense_type || 'No reason provided',
    description: expense.description || expense.reason,
    amount: parseFloat(expense.expense_amount || expense.amount) || 0,
    status: expense.status || 'Pending',
    project_id: expense.project_id,
    category: expense.category || expense.expense_type,
    remarks: expense.remarks || '',
    
    expense_image: fullImageUrl,
    expense_image_path: expenseImagePath,
    expenseImage: fullImageUrl,
    image_url: fullImageUrl,
    photo: fullImageUrl,
    other_details: expense.other_details || expense.otherDetails || '',
    otherDetails: expense.other_details || expense.otherDetails || '',
    originalData: expense,
  };
}, []);



useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!projectId) {
      setExpenses([]);
      setLoading(false);
      return;
    }

    if (currentProjectIdRef.current === projectId && hasFetchedRef.current) {
      return;
    }

    const loadExpenses = async () => {
      currentProjectIdRef.current = projectId;
      hasFetchedRef.current = true;

      try {
        const lookup = await fetchEmployeeLookup();
        
        if (!isMountedRef.current) return;
        
        setEmployeeLookup(lookup);

        if (project?.expenses && Array.isArray(project.expenses)) {
          const formatted = project.expenses
            .filter(expense => String(expense.project_id) === String(projectId))
            .map((expense, index) => formatExpenseData(expense, index, lookup));
          
          if (isMountedRef.current) {
            setExpenses(formatted);
            setLoading(false);
          }
        } else {
          const response = await fetch(`${API_URL}/projects/get_project_expense?project_id=${projectId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'cors',
          });

          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }

          const result = await response.json();
          const expensesData = result.project_expenses || [];

          const formatted = expensesData
            .filter(expense => String(expense.project_id) === String(projectId))
            .map((expense, index) => formatExpenseData(expense, index, lookup));

          if (isMountedRef.current) {
            setExpenses(formatted);
            setLoading(false);
          }
        }
      } catch (error) {
     
        if (isMountedRef.current) {
          
          setExpenses([]);
          setLoading(false);
        }
      }
    };

    loadExpenses();
  }, [projectId, project, fetchEmployeeLookup, formatExpenseData]);

  const handleBackClick = useCallback(() => {
    if (goBack && typeof goBack === 'function') {
      goBack();
    }
  }, [goBack]);

  const handleAddExpenseClick = useCallback(() => {
    setShowAddExpenseForm(true);
  }, []);

  const handleCloseExpenseForm = useCallback(() => {
    setShowAddExpenseForm(false);
  }, []);

  const handleUpdateClick = useCallback((expense) => {
    setSelectedExpenseForUpdate(expense);
    setShowUpdateExpenseForm(true);
  }, []);

  const handleCloseUpdateForm = useCallback(() => {
    setShowUpdateExpenseForm(false);
    setSelectedExpenseForUpdate(null);
  }, []);

  const handleUpdateSubmit = useCallback((updatedExpenseData) => {
    setExpenses(prevExpenses => 
      prevExpenses.map(expense => 
        expense.id === updatedExpenseData.id 
          ? { ...expense, ...updatedExpenseData }
          : expense
      )
    );

    if (onExpenseUpdated) {
      onExpenseUpdated(updatedExpenseData, projectId);
    }
    
    setShowUpdateExpenseForm(false);
    setSelectedExpenseForUpdate(null);
  }, [onExpenseUpdated, projectId]);

const handleExpenseSubmit = useCallback((newExpense) => {
  const employeeName = newExpense.employee_name || employeeLookup[newExpense.employee_id] || `Employee ${newExpense.employee_id}`;
  

  const imagePath = newExpense.expense_image || '';
  const fullImageUrl = imagePath ? getImageUrl(imagePath) : '';
  
 
  
  const expense = {
    id: Date.now(),
    expense_id: Date.now(),
    employeeId: newExpense.employee_id,
    employeeName: employeeName,
    date: newExpense.date || new Date().toLocaleDateString(),
    reason: newExpense.reason || newExpense.description,
    description: newExpense.reason || newExpense.description,
    amount: parseFloat(newExpense.amount) || 0,
    status: 'Pending',
    project_id: projectId,
    

    expense_image: fullImageUrl,
    expense_image_path: imagePath,
    expenseImage: fullImageUrl,
    image_url: fullImageUrl,
    photo: fullImageUrl,
    
    other_details: newExpense.other_details || newExpense.otherDetails || '',
    otherDetails: newExpense.other_details || newExpense.otherDetails || '',
    originalData: newExpense
  };
  
  setExpenses(prevExpenses => [...prevExpenses, expense]);
  setShowAddExpenseForm(false);
  
  if (onExpenseAdded) {
    onExpenseAdded(expense, projectId);
  }
}, [employeeLookup, projectId, onExpenseAdded]);





  const handleEditClick = useCallback((expense) => {
    setEditingId(expense.id);
    setEditedExpense({
      employeeName: expense.employeeName,
      reason: expense.reason,
      amount: expense.amount,
      status: expense.status
    });
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditedExpense(prev => ({
      ...prev,
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    }));
  }, []);

  const handleSaveClick = useCallback((expenseId) => {
    const updatedExpenseData = {
      ...editedExpense,
      id: expenseId,
      expense_id: expenseId
    };

    setExpenses(prevExpenses => 
      prevExpenses.map(expense => 
        expense.id === expenseId 
          ? { ...expense, ...editedExpense }
          : expense
      )
    );
    
    if (onExpenseUpdated) {
      onExpenseUpdated(updatedExpenseData, projectId);
    }
    
    setEditingId(null);
    setEditedExpense({});
  }, [editedExpense, onExpenseUpdated, projectId]);

  const handleCancelClick = useCallback(() => {
    setEditingId(null);
    setEditedExpense({});
  }, []);

  const handleViewExpense = useCallback((expense) => {
    setViewingExpense(expense);
    setShowViewModal(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewingExpense(null);
  }, []);

const handleDeleteExpense = useCallback(async (expense) => {
    const expenseId = expense.expense_id || expense.id;
    const expenseProjectId = expense.project_id || projectId;

    if (!expenseId) {
     
      alert('Cannot delete: Expense ID is missing');
      return;
    }

    if (!expenseProjectId) {

      alert('Cannot delete: Project ID is missing');
      return;
    }

    
    if (!window.confirm(
      `Are you sure you want to delete this expense?\n\n` +
      `Employee: ${expense.employeeName}\n` +
      `Amount: ₹${expense.amount}\n` +
      `Reason: ${expense.reason}\n` +
      `Date: ${expense.date}`
    )) {
      return;
    }

    setDeletingId(expenseId);

    try {
      const requestBody = {
        expense_id: parseInt(expenseId),
        project_id: parseInt(expenseProjectId)
      };

     
      const response = await fetch(`${API_URL}/projects/delete_project_expense`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });

     
      
     
      let result;
      const responseText = await response.text();
     

      try {
        result = JSON.parse(responseText);
      
      } catch (parseError) {
     
        throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 100)}`);
      }



     
      if (response.ok && result.status === 'success') {
        
        setExpenses(prevExpenses => 
          prevExpenses.filter(exp => exp.id !== expense.id)
        );
        
       
        if (onExpenseDeleted) {
          onExpenseDeleted(expenseId, expenseProjectId);
        }
        
        alert('Expense deleted successfully!');
    
        
      } else {
        
        const errorMessage = result.message || result.error || 'Unknown error occurred';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
 
      
      
      let userMessage = 'Failed to delete expense';
      
      if (error.message.includes('Failed to fetch')) {
        userMessage = 'Network error: Could not connect to server. Please check your internet connection.';
      } else if (error.message.includes('not found')) {
        userMessage = 'Expense not found. It may have already been deleted.';
      } else if (error.message) {
        userMessage = `Failed to delete expense: ${error.message}`;
      }
      
      alert(userMessage);
      setError(userMessage);
      
     
      if (error.message.includes('not found')) {
       
       
        hasFetchedRef.current = false;
        currentProjectIdRef.current = null;
      }
      
    } finally {
      setDeletingId(null);
    }
  }, [projectId, onExpenseDeleted]);
  if (loading) {
    return (
      <div className="EXPEN-00">
        <div className="EXPEN-01">
          <h2 className="EXPEN-02">{projectName}</h2>
          <div className="EXPEN-loading">
            Loading expenses...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="EXPEN-00">
      <div className="EXPEN-01">
        <div className="EXPEN-02">
          <div className="breadcrumb">Expenses -</div>
          <div className="project-title">{projectName}</div>
        </div>
        
        {error && (
          <div className="EXPEN-error">
            {error}
            <button 
              onClick={() => window.location.reload()}
              className="EXPEN-retry-btn"
            >
              Retry
            </button>
          </div>
        )}
        
        <div className="EXPEN-03">
          <div className="EXPEN-04">
            <div className="EXPEN-30"><FaSearch /></div>
            <input
              type="text"
              placeholder="Search Expenses Details"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="EXPEN-05"
            />
          </div>
       



          {getCurrentUserRole() !== 'CEO' && (
  <button 
    className="EXPEN-09"
    onClick={handleAddExpenseClick}
  >
    Add Expenses
  </button>
)}
        </div>

        <div className="EXPEN-filters">
          <div className="EXPEN-filter-group">
  <label className="EXPEN-filter-label">Employee Name:</label>
  <select 
    value={selectedEmployee} 
    onChange={(e) => setSelectedEmployee(e.target.value)}
    className="EXPEN-filter-select"
  >
    {(getCurrentUserRole() === 'CEO' || getCurrentUserRole() === 'ADMIN') && (
      <option value="">All Employees</option>
    )}
    {getAvailableEmployees().map(emp => (
      <option key={emp.id} value={emp.id}>
        {emp.name}
      </option>
    ))}
  </select>
</div>

          <div className="EXPEN-filter-group">
            <label className="EXPEN-filter-label">From Date:</label>
            <input
              type="date"
              value={selectedFromDate}
              onChange={(e) => setSelectedFromDate(e.target.value)}
              className="EXPEN-filter-date"
            />
          </div>

          <div className="EXPEN-filter-group">
            <label className="EXPEN-filter-label">To Date:</label>
            <input
              type="date"
              value={selectedToDate}
              onChange={(e) => setSelectedToDate(e.target.value)}
              className="EXPEN-filter-date"
              min={selectedFromDate}
            />
          </div>

          <div className="EXPEN-filter-group">
            <label className="EXPEN-filter-label">Reason:</label>
            <select 
              value={selectedReason} 
              onChange={(e) => setSelectedReason(e.target.value)}
              className="EXPEN-filter-select"
            >
              <option value="">All Reasons</option>
              {getAvailableReasons().map(reason => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          <div className="EXPEN-filter-group">
            <label className="EXPEN-filter-label">Status:</label>
            <select 
              value={selectedStatus} 
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="EXPEN-filter-select"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        {selectedEmployee && (
          <div className="EXPEN-employee-stats">
            <span className="EXPEN-stats-label">Total Claims by {getAvailableEmployees().find(emp => String(emp.id) === selectedEmployee)?.name}:</span>
            <span className="EXPEN-stats-value">{getEmployeeClaimCount(selectedEmployee)}</span>
          </div>
        )}

        <div className="EXPEN-10">
          <table className="EXPEN-11">
            <thead>
              <tr className="EXPEN-12">
                <th className="EXPEN-13">EMPLOYEE NAME</th>
                <th className="EXPEN-14">DATE</th>
                <th className="EXPEN-15">REASON</th>
                <th className="EXPEN-16">AMOUNT</th>
                <th className="EXPEN-17">STATUS</th>
                <th className="EXPEN-24">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="EXPEN-no-data">
                    {error ? 
                      'Unable to load expenses' : 
                      (searchTerm || selectedEmployee || selectedFromDate || selectedToDate || selectedReason || selectedStatus ? 
                        'No expenses match your filters' : 
                        'No expenses found for this project'
                      )
                    }
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense, index) => (
                  <tr key={expense.id || index} className="EXPEN-18">
                    <td className="EXPEN-19">
                      {editingId === expense.id ? (
                        <input
                          type="text"
                          value={editedExpense.employeeName || expense.employeeName}
                          onChange={(e) => handleInputChange('employeeName', e.target.value)}
                          className="EXPEN-edit-input"
                        />
                      ) : (
                        expense.employeeName
                      )}
                    </td>
                    <td className="EXPEN-20">{expense.date}</td>
                    <td className="EXPEN-21">
                      {editingId === expense.id ? (
                        <input
                          type="text"
                          value={editedExpense.reason || expense.reason}
                          onChange={(e) => handleInputChange('reason', e.target.value)}
                          className="EXPEN-edit-input"
                        />
                      ) : (
                        expense.reason
                      )}
                    </td>
                    <td className="EXPEN-22">
                      {editingId === expense.id ? (
                        <input
                          type="number"
                          value={editedExpense.amount || expense.amount}
                          onChange={(e) => handleInputChange('amount', e.target.value)}
                          className="EXPEN-edit-input"
                        />
                      ) : (
                        `₹${expense.amount.toLocaleString()}`
                      )}
                    </td>
                    <td className={`EXPEN-23 ${getStatusClass(expense.status)}`}>
                      {editingId === expense.id ? (
                        <select
                          value={editedExpense.status || expense.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                          className="EXPEN-edit-select"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      ) : (
                        <div className="EXPEN-status-container">
                          {getStatusIcon(expense.status)}
                        </div>
                      )}
                    </td>
                    <td className="EXPEN-24">
                      {editingId === expense.id ? (
                        <div className="EXPEN-action-buttons">
                          <button 
                            onClick={() => handleSaveClick(expense.id)}
                            className="EXPEN-save-btn"
                          >
                            Save
                          </button>
                          <button 
                            onClick={handleCancelClick}
                            className="EXPEN-cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="EXPEN-action-buttons">
                          {getCurrentUserRole() !== 'CEO' && (
                            <button 
                              onClick={() => handleDeleteExpense(expense)}
                              className="EXPEN-delete-btn"
                              disabled={deletingId === expense.id}
                              title="Delete Expense"
                            >
                              <FaTrash />
                            </button>
                          )}
                          <button 
                            onClick={() => handleUpdateClick(expense)}
                            className="EXPEN-update-btn"
                            disabled={deletingId === expense.id}
                            title="Update Expense"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            onClick={() => handleViewExpense(expense)}
                            className="EXPEN-view-btn"
                            disabled={deletingId === expense.id}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showViewModal && viewingExpense && (
          <div className="EXPEN-modal-overlay" onClick={handleCloseViewModal}>
            <div className="EXPEN-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="EXPEN-modal-header">
                <h3>Expense Details</h3>
                <button className="EXPEN-modal-close" onClick={handleCloseViewModal}>×</button>
              </div>
              <div className="EXPEN-modal-body">
                <div className="EXPEN-detail-row">
                  <label>Employee Name:</label>
                  <span>{viewingExpense.employeeName}</span>
                </div>
                <div className="EXPEN-detail-row">
                  <label>Date:</label>
                  <span>{viewingExpense.date}</span>
                </div>
                <div className="EXPEN-detail-row">
                  <label>Reason:</label>
                  <span>{viewingExpense.reason}</span>
                </div>
                <div className="EXPEN-detail-row">
                  <label>Amount:</label>
                  <span>₹{viewingExpense.amount.toLocaleString()}</span>
                </div>
                <div className="EXPEN-detail-row">
                  <label>Status:</label>
                  <span className={getStatusClass(viewingExpense.status)}>
                    {viewingExpense.status}
                  </span>
                </div>
                {viewingExpense.remarks && (
                  <div className="EXPEN-detail-row">
                    <label>Remarks:</label>
                    <span className="EXPEN-remarks-text">{viewingExpense.remarks}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="EXPEN-27">
          <button 
            className="EXPEN-26" 
            onClick={handleBackClick}
            type="button"
          >
            Back
          </button>

          <button 
            className="EXPEN-25"
          >
            Total: ₹{totalAmount.toLocaleString()}
          </button>
        </div>
      </div>

      {showAddExpenseForm && (
        <ExpenseForm 
          onClose={handleCloseExpenseForm}
          onSubmit={handleExpenseSubmit}
          projectId={projectId} 
          employeeId={1}
          projectName={projectName}
        />
      )}

      {showUpdateExpenseForm && selectedExpenseForUpdate && (
        <UpdateExpenses
          expense={selectedExpenseForUpdate}
          onClose={handleCloseUpdateForm}
          onSubmit={handleUpdateSubmit}
          projectId={projectId}
          projectName={projectName}
        />
      )}
    </div>
  );
};

export default ExpenseList;